import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import { IconButton } from '@upmc/ims.component-library';
import { Tooltip } from '../../UI/MDC/Tooltip/Tooltip';
import { CONTENT_ALIGNMENT } from '../../UI/MDC/Tooltip/_types';
import List, {
	ListItem,
	MDCListItemText,
	MDCListDivider,
	MDCListItemMeta,
} from '../../UI/MDC/List/List';
import Card from '../../UI/MDC/Card/Card';
import MaterialIcon from '../../UI/MDC/Icon/Icon';
import { Subtitle1 } from '../../UI/MDC/Typography/Typography';
import { CSS_CLASSES } from '../_constants';
import { getNotifications } from '../_api';
import { raiseErrorBanner } from '../../MessageDispatch/messageDispatch';
import classes from './Icon.module.scss';
import { UserContext } from 'context/User/UserContext';
import { determineIfExternal, getPath } from 'routes/_utils';

/**
 * Functional component for rendering end-user database notifications (alerts).
 *
 * @param {Object} props JSX attributes passed to component.
 * @param {string} props.className nullable CSS class forwarded to {@link IconButton} instance.
 * @returns {React.JSX.Element} rendered component.
 */
const NotificationIcon = ({ align = CONTENT_ALIGNMENT.BOTTOM }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.impersonationProfile ?? contextUser.userInfo.userProfile;

	const [sIsCardOpen, setIsCardOpen] = useState(false);
	const [notifications, setNotifications] = useState(null);

	/**
	 * Asynchronous hook on identifiable user profile. Retrieves and stores user notifications in local state.
	 * @returns void
	 */
	useEffect(() => {
		/**
		 * Asynchronously retrieves, stores notifications.
		 * @param {number} id user unique identifier.
		 * @returns void
		 */
		const render = async id => {
			try {
				const notifications = await getNotifications(id, dispatch);
				setNotifications(notifications);
			} catch (error) {
				raiseErrorBanner(error.cause ? error.cause : error, dispatch);
			}
		};

		if (user.ID) {
			render(user.ID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	return (
		<>
			<Tooltip align={align} content='Notifications'>
				<IconButton
					size='large'
					aria-label='notifications'
					color='inherit'
					className={classes[CSS_CLASSES.ICON]}
					id={CSS_CLASSES.ICON}
					onBlur={e => {
						const elNotificationCard = document.getElementById(
							CSS_CLASSES.CARD
						);
						const elFocused = e.relatedTarget;
						setIsCardOpen(
							elFocused && elNotificationCard?.contains(elFocused)
						);
					}}
					onClick={() => setIsCardOpen(!sIsCardOpen)}
				>
					{notifications?.length > 0 ? (
						<Badge
							badgeContent={notifications?.length}
							color='error'
						>
							<NotificationsIcon />
						</Badge>
					) : (
						<NotificationsIcon />
					)}
				</IconButton>
			</Tooltip>
			{sIsCardOpen && (
				<Card
					className={classnames([
						classes[CSS_CLASSES.CARD],
						{
							[classes[CSS_CLASSES.CARD_EMPTY]]:
								notifications?.length < 1,
						},
					])}
					id={CSS_CLASSES.CARD}
				>
					{notifications?.length > 0 ? (
						<List
							className={classes[CSS_CLASSES.LIST]}
							dense
							handleSelect={index => {
								setIsCardOpen(false);
								const path = notifications[index].Path?.trim();
								if (path) {
									if (determineIfExternal(path)) {
										window.top.location.href = path;
									} else {
										history.push(getPath(path));
									}
								}
							}}
							orientation='vertical'
							singleSelection
							xlarge
						>
							{notifications?.map((notification, index) => {
								return (
									<React.Fragment key={notification.Id}>
										<ListItem>
											<MDCListItemText
												className={
													classes[
														CSS_CLASSES
															.LIST_ITEM_TEXT
													]
												}
												primaryText={notification.Title}
											/>
											<MDCListItemMeta
												meta={
													<MaterialIcon icon='chevron_right' />
												}
											/>
										</ListItem>
										{index !==
											notifications?.length - 1 && (
											<MDCListDivider />
										)}
									</React.Fragment>
								);
							})}
						</List>
					) : (
						<Subtitle1>You're all caught up!</Subtitle1>
					)}
				</Card>
			)}
		</>
	);
};

export default NotificationIcon;
